import { useState, useMemo } from "react";
import { z } from "zod";
import { RichText } from "../RichText";
import { useRenderContext, type BlogPostEntry, type BlogPostTag } from "../RenderContext";
import { globalSectionSchema, subHeadingLevel } from "../globalProps";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Latest Insights"),
  subheading: z.string().optional(),
  layout: z.enum(["grid", "list", "editorial"]).default("grid"),
  postsPerPage: z.number().int().min(1).default(9),
  showTagFilter: z.boolean().default(true),
  tagFilter: z.string().optional(),
  showPagination: z.boolean().default(true),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  showDate: z.boolean().default(true),
  showAuthor: z.boolean().default(true),
  readMoreLabel: z.string().optional(),
  imageAspect: z.enum(["video", "4:3", "1:1"]).default("video"),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

function formatDateSlug(dateStr: string | null | undefined, slug: string): string {
  if (!dateStr) return slug;
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}-${slug}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const gridColClasses: Record<number, string> = {
  2: "grid-cols-1 @md:grid-cols-2",
  3: "grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3",
  4: "grid-cols-1 @md:grid-cols-2 @lg:grid-cols-4",
};

const imageAspectClasses: Record<string, string> = {
  video: "aspect-video",
  "4:3": "aspect-[4/3]",
  "1:1": "aspect-square",
};

/** Collect all unique tags from posts. */
function collectTags(posts: BlogPostEntry[]): BlogPostTag[] {
  const seen = new Map<string, BlogPostTag>();
  for (const post of posts) {
    for (const tag of post.tags || []) {
      if (!seen.has(tag.slug)) {
        seen.set(tag.slug, tag);
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/* ---------- Tag Filter Rail ---------- */

function TagRail({
  tags,
  activeTag,
  onTagChange,
}: {
  tags: BlogPostTag[];
  activeTag: string | null;
  onTagChange: (slug: string | null) => void;
}) {
  if (tags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-10 justify-center">
      <button
        onClick={() => onTagChange(null)}
        className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
          activeTag === null
            ? "bg-primary text-on-primary"
            : "bg-surface text-text-muted hover:bg-surface/80"
        }`}
      >
        All
      </button>
      {tags.map((tag) => (
        <button
          key={tag.slug}
          onClick={() => onTagChange(activeTag === tag.slug ? null : tag.slug)}
          className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeTag === tag.slug
              ? "bg-primary text-on-primary"
              : "bg-surface text-text-muted hover:bg-surface/80"
          }`}
        >
          {tag.name}
        </button>
      ))}
    </div>
  );
}

/* ---------- Pagination ---------- */

function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  // Show up to 5 page buttons centered around the current page
  const pages: number[] = [];
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <nav className="flex items-center justify-center gap-2 mt-12" aria-label="Pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 rounded text-sm font-medium text-text-muted hover:bg-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Previous page"
      >
        Previous
      </button>
      {pages[0] > 1 && (
        <>
          <button
            onClick={() => onPageChange(1)}
            className="w-9 h-9 rounded text-sm font-medium text-text-muted hover:bg-surface transition-colors"
          >
            1
          </button>
          {pages[0] > 2 && (
            <span className="text-text-muted text-sm px-1">...</span>
          )}
        </>
      )}
      {pages.map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`w-9 h-9 rounded text-sm font-medium transition-colors ${
            page === currentPage
              ? "bg-primary text-on-primary"
              : "text-text-muted hover:bg-surface"
          }`}
          aria-current={page === currentPage ? "page" : undefined}
        >
          {page}
        </button>
      ))}
      {pages[pages.length - 1] < totalPages && (
        <>
          {pages[pages.length - 1] < totalPages - 1 && (
            <span className="text-text-muted text-sm px-1">...</span>
          )}
          <button
            onClick={() => onPageChange(totalPages)}
            className="w-9 h-9 rounded text-sm font-medium text-text-muted hover:bg-surface transition-colors"
          >
            {totalPages}
          </button>
        </>
      )}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 rounded text-sm font-medium text-text-muted hover:bg-surface transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Next page"
      >
        Next
      </button>
    </nav>
  );
}

/* ---------- Grid Layout ---------- */

function GridLayout({
  posts,
  blogBasePath,
  SubH,
  columns,
  showDate,
  showAuthor,
  readMoreLabel,
  imageAspect,
}: {
  posts: BlogPostEntry[];
  blogBasePath: string;
  SubH: "h2" | "h3" | "h4";
  columns: number;
  showDate: boolean;
  showAuthor: boolean;
  readMoreLabel?: string;
  imageAspect: string;
}) {
  const aspectClass = imageAspectClasses[imageAspect] || imageAspectClasses.video;
  return (
    <div className={`grid ${gridColClasses[columns]} gap-8`}>
      {posts.map((post) => (
        <a
          key={post.id}
          href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
          className="bg-white text-text rounded-lg shadow-md overflow-hidden group"
        >
          {post.featuredImageUrl && (
            <div className={`${aspectClass} overflow-hidden`}>
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-5">
            <SubH className="font-semibold text-text mb-2 group-hover:text-primary transition-colors">
              {post.title}
            </SubH>
            {post.summary && (
              <p className="text-text-muted text-sm line-clamp-2">
                {post.summary}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              {showDate && post.publishAt && (
                <time className="text-sm text-text-muted">
                  {formatDate(post.publishAt)}
                </time>
              )}
              {showDate && showAuthor && post.publishAt && post.author && (
                <span className="text-text-muted text-sm">·</span>
              )}
              {showAuthor && post.author && (
                <span className="text-sm text-text-muted">{post.author}</span>
              )}
            </div>
            {readMoreLabel && (
              <span className="inline-block text-sm font-medium text-primary mt-3 group-hover:underline">
                {readMoreLabel}
              </span>
            )}
          </div>
        </a>
      ))}
    </div>
  );
}

/* ---------- List Layout ---------- */

function ListLayout({
  posts,
  blogBasePath,
  SubH,
  showDate,
  showAuthor,
}: {
  posts: BlogPostEntry[];
  blogBasePath: string;
  SubH: "h2" | "h3" | "h4";
  showDate: boolean;
  showAuthor: boolean;
}) {
  return (
    <div className="space-y-0 divide-y divide-current/10">
      {posts.map((post) => (
        <a
          key={post.id}
          href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
          className="flex items-start gap-5 py-5 group"
        >
          {post.featuredImageUrl && (
            <div className="w-28 h-20 @md:w-36 @md:h-24 flex-shrink-0 overflow-hidden rounded-lg">
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <SubH className="font-semibold text-text group-hover:text-primary transition-colors">
              {post.title}
            </SubH>
            {post.summary && (
              <p className="text-text-muted text-sm line-clamp-2 mt-1">
                {post.summary}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              {showDate && post.publishAt && (
                <time className="text-sm text-text-muted">
                  {formatDate(post.publishAt)}
                </time>
              )}
              {showDate && showAuthor && post.publishAt && post.author && (
                <span className="text-text-muted text-sm">·</span>
              )}
              {showAuthor && post.author && (
                <span className="text-sm text-text-muted">{post.author}</span>
              )}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

/* ---------- Editorial Layout ---------- */

/**
 * Pattern array from the legacy blog.tsx mosaic. After the featured hero
 * (post 0) and secondary trio (posts 1-3), remaining posts cycle through
 * this pattern: pair, pair, wide, text, text, text, pair, pair, wide, text, text.
 */
const EDITORIAL_PATTERN = [
  "pair", "pair", "wide", "text", "text", "text",
  "pair", "pair", "wide", "text", "text",
] as const;

type PatternSlot = (typeof EDITORIAL_PATTERN)[number];

function EditorialLayout({
  posts,
  blogBasePath,
  SubH,
  showDate,
  showAuthor,
}: {
  posts: BlogPostEntry[];
  blogBasePath: string;
  SubH: "h2" | "h3" | "h4";
  showDate: boolean;
  showAuthor: boolean;
}) {
  const hero = posts[0];
  const secondary = posts.slice(1, 4);
  const rest = posts.slice(4);

  /** Build the pattern-row groups from remaining posts. */
  function buildPatternRows(items: BlogPostEntry[]): { type: PatternSlot; posts: BlogPostEntry[] }[] {
    const rows: { type: PatternSlot; posts: BlogPostEntry[] }[] = [];
    let patternIdx = 0;
    let i = 0;

    while (i < items.length) {
      const slot = EDITORIAL_PATTERN[patternIdx % EDITORIAL_PATTERN.length];

      if (slot === "pair") {
        // Pairs consume 2 posts per "pair" entry, but each "pair" slot in the
        // pattern represents ONE post in a 2-column row. Two consecutive "pair"
        // slots form a visual row. Collect 2 posts when we see the first "pair".
        const pair = items.slice(i, i + 2);
        rows.push({ type: "pair", posts: pair });
        i += 2;
        // Skip the next "pair" slot since we consumed both posts already
        patternIdx += 2;
      } else if (slot === "wide") {
        rows.push({ type: "wide", posts: [items[i]] });
        i += 1;
        patternIdx += 1;
      } else {
        // "text" — collect up to 3 consecutive text items as one row
        const textPosts: BlogPostEntry[] = [];
        while (
          i < items.length &&
          textPosts.length < 3 &&
          EDITORIAL_PATTERN[(patternIdx + textPosts.length) % EDITORIAL_PATTERN.length] === "text"
        ) {
          textPosts.push(items[i]);
          i += 1;
        }
        rows.push({ type: "text", posts: textPosts });
        patternIdx += textPosts.length;
      }
    }

    return rows;
  }

  const patternRows = rest.length > 0 ? buildPatternRows(rest) : [];

  return (
    <div className="space-y-10">
      {/* Featured hero card */}
      {hero && (
        <a
          href={`${blogBasePath}/${formatDateSlug(hero.publishAt, hero.slug)}`}
          className="block rounded-xl overflow-hidden group bg-white text-text shadow-md"
        >
          {hero.featuredImageUrl ? (
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src={hero.featuredImageUrl}
                alt={hero.title}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
              />
            </div>
          ) : (
            <div className="aspect-[16/9] bg-primary/10" />
          )}
          <div className="p-6 @md:p-8">
            <div className="flex items-center gap-2 mb-3">
              {showDate && hero.publishAt && (
                <time className="text-sm text-text-muted">{formatDate(hero.publishAt)}</time>
              )}
              {showDate && showAuthor && hero.publishAt && hero.author && (
                <span className="text-text-muted text-sm">·</span>
              )}
              {showAuthor && hero.author && (
                <span className="text-sm text-text-muted">{hero.author}</span>
              )}
            </div>
            <SubH className="text-2xl @md:text-3xl font-bold text-text mb-3 group-hover:text-primary transition-colors">
              {hero.title}
            </SubH>
            {hero.summary && (
              <p className="text-text-muted leading-relaxed max-w-3xl line-clamp-3">
                {hero.summary}
              </p>
            )}
          </div>
        </a>
      )}

      {/* Secondary trio */}
      {secondary.length > 0 && (
        <div className="grid grid-cols-1 @md:grid-cols-3 gap-6">
          {secondary.map((post) => (
            <a
              key={post.id}
              href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
              className="bg-white text-text rounded-lg shadow-md overflow-hidden group"
            >
              {post.featuredImageUrl ? (
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={post.featuredImageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-surface" />
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {showDate && post.publishAt && (
                    <time className="text-xs text-text-muted">{formatDate(post.publishAt)}</time>
                  )}
                  {showDate && showAuthor && post.publishAt && post.author && (
                    <span className="text-text-muted text-xs">·</span>
                  )}
                  {showAuthor && post.author && (
                    <span className="text-xs text-text-muted">{post.author}</span>
                  )}
                </div>
                <SubH className="font-semibold text-text group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </SubH>
                {post.summary && (
                  <p className="text-text-muted text-sm line-clamp-2 mt-1">
                    {post.summary}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Pattern rows */}
      {patternRows.map((row, rowIdx) => {
        if (row.type === "pair") {
          return (
            <div key={rowIdx} className="grid grid-cols-1 @md:grid-cols-2 gap-6">
              {row.posts.map((post) => (
                <a
                  key={post.id}
                  href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
                  className="flex gap-4 bg-white text-text rounded-lg shadow-md overflow-hidden group p-4"
                >
                  {post.featuredImageUrl ? (
                    <div className="w-28 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={post.featuredImageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="w-28 h-24 flex-shrink-0 rounded-lg bg-surface" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {showDate && post.publishAt && (
                        <time className="text-xs text-text-muted">{formatDate(post.publishAt)}</time>
                      )}
                      {showDate && showAuthor && post.publishAt && post.author && (
                        <span className="text-text-muted text-xs">·</span>
                      )}
                      {showAuthor && post.author && (
                        <span className="text-xs text-text-muted">{post.author}</span>
                      )}
                    </div>
                    <SubH className="font-semibold text-text group-hover:text-primary transition-colors line-clamp-2 text-sm">
                      {post.title}
                    </SubH>
                    {post.summary && (
                      <p className="text-text-muted text-xs line-clamp-2 mt-1">
                        {post.summary}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          );
        }

        if (row.type === "wide") {
          const post = row.posts[0];
          return (
            <a
              key={rowIdx}
              href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
              className="grid grid-cols-1 @md:grid-cols-2 bg-white text-text rounded-lg shadow-md overflow-hidden group"
            >
              {post.featuredImageUrl ? (
                <div className="aspect-video @md:aspect-auto overflow-hidden">
                  <img
                    src={post.featuredImageUrl}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="aspect-video @md:aspect-auto bg-surface" />
              )}
              <div className="p-5 @md:p-6 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-2">
                  {showDate && post.publishAt && (
                    <time className="text-sm text-text-muted">{formatDate(post.publishAt)}</time>
                  )}
                  {showDate && showAuthor && post.publishAt && post.author && (
                    <span className="text-text-muted text-sm">·</span>
                  )}
                  {showAuthor && post.author && (
                    <span className="text-sm text-text-muted">{post.author}</span>
                  )}
                </div>
                <SubH className="text-xl font-bold text-text mb-2 group-hover:text-primary transition-colors">
                  {post.title}
                </SubH>
                {post.summary && (
                  <p className="text-text-muted leading-relaxed line-clamp-3">
                    {post.summary}
                  </p>
                )}
              </div>
            </a>
          );
        }

        // "text" — compact text-only cards in a 3-column grid
        return (
          <div key={rowIdx} className="grid grid-cols-1 @md:grid-cols-3 gap-6">
            {row.posts.map((post) => (
              <a
                key={post.id}
                href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
                className="p-5 bg-white text-text rounded-lg shadow-md group"
              >
                <div className="flex items-center gap-2 mb-2">
                  {showDate && post.publishAt && (
                    <time className="text-xs text-text-muted">{formatDate(post.publishAt)}</time>
                  )}
                  {showDate && showAuthor && post.publishAt && post.author && (
                    <span className="text-text-muted text-xs">·</span>
                  )}
                  {showAuthor && post.author && (
                    <span className="text-xs text-text-muted">{post.author}</span>
                  )}
                </div>
                <SubH className="font-semibold text-text group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </SubH>
                {post.summary && (
                  <p className="text-text-muted text-sm line-clamp-2 mt-2">
                    {post.summary}
                  </p>
                )}
              </a>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const ctx = useRenderContext();
  const blogBasePath = ctx.blogBasePath || "/blog";
  const SubH = subHeadingLevel(safe.headingLevel);

  const allPosts: BlogPostEntry[] = ctx.blogPosts || [];

  // Derive available tags from the full post set (before tag filtering)
  const availableTags = useMemo(() => collectTags(allPosts), [allPosts]);

  // State for tag filter and page (client-side)
  const [activeTag, setActiveTag] = useState<string | null>(
    ctx.urlTagFilter || safe.tagFilter || null,
  );
  const [currentPage, setCurrentPage] = useState(1);

  // Filter posts by tag
  const filteredPosts = useMemo(() => {
    if (!activeTag) return allPosts;
    return allPosts.filter(
      (post) => post.tags?.some((t) => t.slug === activeTag),
    );
  }, [allPosts, activeTag]);

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / safe.postsPerPage);
  const pageStart = (currentPage - 1) * safe.postsPerPage;
  const pagePosts = filteredPosts.slice(pageStart, pageStart + safe.postsPerPage);

  // Reset to page 1 when tag changes
  const handleTagChange = (slug: string | null) => {
    setActiveTag(slug);
    setCurrentPage(1);
  };

  if (allPosts.length === 0) {
    return null;
  }

  return (
    <SectionShell globals={safe}>
      <div className="text-center mb-12">
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold mb-4"
          data-prop="heading"
        />
        {safe.subheading && (
          <RichText
            as="div"
            html={safe.subheading}
            className="text-lg opacity-80 max-w-2xl mx-auto"
            data-prop="subheading"
          />
        )}
      </div>

      {safe.showTagFilter && availableTags.length > 0 && (
        <TagRail
          tags={availableTags}
          activeTag={activeTag}
          onTagChange={handleTagChange}
        />
      )}

      {pagePosts.length === 0 ? (
        <p className="text-center text-text-muted py-8">
          No posts found{activeTag ? " for this tag" : ""}.
        </p>
      ) : safe.layout === "editorial" ? (
        <EditorialLayout
          posts={pagePosts}
          blogBasePath={blogBasePath}
          SubH={SubH}
          showDate={safe.showDate}
          showAuthor={safe.showAuthor}
        />
      ) : safe.layout === "list" ? (
        <ListLayout
          posts={pagePosts}
          blogBasePath={blogBasePath}
          SubH={SubH}
          showDate={safe.showDate}
          showAuthor={safe.showAuthor}
        />
      ) : (
        <GridLayout
          posts={pagePosts}
          blogBasePath={blogBasePath}
          SubH={SubH}
          columns={safe.columns}
          showDate={safe.showDate}
          showAuthor={safe.showAuthor}
          readMoreLabel={safe.readMoreLabel}
          imageAspect={safe.imageAspect}
        />
      )}

      {safe.showPagination && totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </SectionShell>
  );
}
